import { useCallback, useEffect, useState } from 'react';
import { devicesAPI } from '../apiClient';

const STORAGE_KEY = 'webPushEnabled';

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
};

export function useWebPush({ enabled = true } = {}) {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const checkSupport = useCallback(async () => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setIsSupported(supported);
    if (!supported) return false;

    const registration = await navigator.serviceWorker.ready.catch(() => null);
    if (!registration) return false;

    const subscription = await registration.pushManager.getSubscription();
    setIsSubscribed(!!subscription);
    return !!subscription;
  }, []);

  useEffect(() => {
    if (!enabled) return;
    checkSupport();
  }, [enabled, checkSupport]);

  const subscribe = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data } = await devicesAPI.getWebPushPublicKey();
      const publicKey = data?.publicKey;
      if (!publicKey) {
        throw new Error('Web push is not configured on the server');
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission was denied');
      }

      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }

      await devicesAPI.registerWebPush({
        subscription: subscription.toJSON(),
        deviceName: navigator.userAgent.includes('Mobile') ? 'Mobile browser' : 'Desktop browser',
      });

      localStorage.setItem(STORAGE_KEY, 'true');
      setIsSubscribed(true);
      return true;
    } catch (err) {
      setError(err.message || 'Failed to enable notifications');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await devicesAPI.unregisterWebPush({ endpoint: subscription.endpoint });
        await subscription.unsubscribe();
      }

      localStorage.removeItem(STORAGE_KEY);
      setIsSubscribed(false);
    } catch (err) {
      setError(err.message || 'Failed to disable notifications');
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isSupported,
    isSubscribed,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    checkSupport,
  };
}

export const shouldPromptWebPush = () => {
  if (localStorage.getItem(STORAGE_KEY) === 'true') return false;
  if (typeof Notification !== 'undefined' && Notification.permission === 'denied') return false;
  return true;
};
